import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, LoadingController, AlertController, IonInput } from '@ionic/angular/lazy';
import { CartService } from 'src/app/services/cart.service';
import { APIService } from 'src/app/services/api.service';
import { Decimal } from 'decimal.js';

@Component({
    selector: 'app-sell-gift-voucher',
    templateUrl: './sell-gift-voucher.page.html',
    styleUrls: ['./sell-gift-voucher.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SellGiftVoucherPage implements OnInit {

    @ViewChild('amount_input', { static: false }) amount_input: IonInput;

    code = null;
    amount = null;
    spinner = null;
    adding_to_cart = false;

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        public loadingController: LoadingController,
        private apiService: APIService,
        private cartService: CartService
    ) { }

    ngOnInit() {
        this.generateCode();
    }

    ionViewDidEnter() {
        // NOTE: we use a timeout here so the DOM gets a chance to load
        setTimeout(() => {
            if(this.amount_input) {
                this.amount_input.setFocus();
            }
        }, 200);
    }

    generateCode() {
        let numbers = "0123456789";
        let length = 16;
        let code = '';
        for (let i = 0; i < length; i++) {
            let random_number = Math.floor(Math.random() * numbers.length);
            code += numbers.substring(random_number, random_number + 1);
        }
        this.code = code;
    }

    close() {
        this.modalController.dismiss();
    }

    addToCart() {
        this.cartService.addGiftVoucherItem(this.code,this.amount).then(() => {
            this.modalController.dismiss();
        })
        .catch(() => {
            this.adding_to_cart = false;
        });
    }

    validateInput() {

        // NOTE: ignore repeat presses while the voucher is being validated/added, otherwise a double tap/Enter adds it twice
        if(!this.adding_to_cart) {

            this.adding_to_cart = true;

            let validated = true;
            this.presentSpinner("Validating voucher").then(async () => {
                if (this.code == null || this.code.trim() === "") {
                    validated = false;
                    this.presentAlert("Please enter a voucher code", "");
                }
                else {
                    await this.apiService.getGiftVoucherByCode(this.code, {}).then((gift_voucher: any) => {
                        if (gift_voucher) {
                            validated = false;
                            this.presentAlert("Already exists", "This gift voucher code already exists, please use a different code.");
                        }
                    })
                    .catch((error: any) => {
                    // We expect a 401 (not found) here, i.e. if a voucher is not found then it is unique and can be created.
                        if (error) {
                            if(error.status === 400) {
                                validated = false;
                                this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to check if this voucher exists.");
                            } else if(error.status === 403) {
                                validated = false;
                                this.presentAlert("Insufficient permissions", "You don't have permission to retrieve a gift voucher.");
                            } else if(error.status === 0) {
                                validated = false;
                                this.presentAlert("Unavailable", "Unfortunately we're unable to check this voucher at this time.");
                            }
                        }
                    });
                }

                let price_regex = /^\d+(\.\d+)?$/;

                // Catch nulls
                if (this.amount == null || this.amount.trim() === "") {
                    validated = false;
                    this.spinner.dismiss();
                    this.presentAlert("No amount entered", "");
                }
                else if (!price_regex.test(String(this.amount).toLowerCase())) {
                    validated = false;
                    this.spinner.dismiss();
                    this.presentAlert("Invalid amount", "");
                }
                else {
                    // ensure 2dp
                    this.amount = Number(new Decimal(this.amount).toDecimalPlaces(2));
                }

                if (validated) {
                    this.spinner.dismiss();
                    this.addToCart();
                }
                else {
                    this.spinner.dismiss();
                    this.adding_to_cart = false;
                }
            });

        }

    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

}
